import moment from 'moment';
import { PLAN_TYPE_IDS, PROVINCES, stateWithPlan2AsPrimary } from "../Utils/PlanUtils";
import planTypes from "../Config/PlanTypes";
import { generateQuote } from "../Quotes/QuoteGenerator";

export const defaultFontSize = 14;
export const checkBoxCharacterSize = 10;
export const checkBoxCharacter = "X";

export const standardFormX = -1;
export const standardFormY = 0;
export const standardFormWidth = 216;
export const standardFormHeight = 279;
export const standardFormCompression = "FAST";
export const standardFormFormat = "PNG";
export const standardQuoteImageWidth = 200;
export const standardQuoteImageHeight = 238;
export const standardQuoteImageX = 5;
export const standardQuoteImageY = 0;

const PTToMM = 0.352778;

// This function will draw text on the PDF, and automatically shrink the font size until the text fits the maxWidth
export function drawText(doc, fontSize, text, x, y, maxWidth) {
    // calculate font size in mm
    let fontSizeInmm = fontSize * PTToMM;

    // Once the text fits, or the font size is reduced to 1, draw the text
    // If you draw text at Y=0 it's actually off the top of the page.  So we add a multiplier of the font size to the Y position to ensure it's on the page.
    doc.text(text, x, y + fontSizeInmm / 2);
}

// This function will draw text on the PDF, and automatically shrink the font size until the text fits the maxWidth
export function drawTextScaled(doc, fontSize, text, x, y, maxWidth) {
    // calculate font size in mm
    let fontSizeInmm = fontSize * PTToMM;

    // set font size
    doc.setFontSize(fontSizeInmm);

    // Once the text fits, or the font size is reduced to 1, draw the text
    // If you draw text at Y=0 it's actually off the top of the page.  So we add a multiplier of the font size to the Y position to ensure it's on the page.
    doc.text(text, x, y + fontSizeInmm / 2);
}

// Draws a white rectangle over a region to hide previously drawn content.
export function drawWhiteBox(doc, x, y, width, height) {
    doc.setFillColor(255, 255, 255);
    doc.rect(x, y, width, height, "F");
}

//Draws text to a named label location based on the configuration for the form
export function drawTextOnLabel(doc, text, labelName, labels, colorOverride = null, bold = false, wrap = false) {

    if (!text) {
        let label = labels['labels'][labelName];

        if (label == undefined) {
            console.error(`Label ${labelName} not found in labels`);
        }
        return;
    }
    text = `${text}`;
    let label = labels['labels'][labelName];

    if (label == undefined) {
        console.error(`Label ${labelName} not found in labels`);
        return;
    }

    let x = +label['x'];
    let y = +label['y'];

    const width = +label['width'];
    const height = +label['height'];

    let textColor = label['textColor'];
    let backgroundColor = label['backgroundColor'];
    let textAlignment = label['textAlignment'];
    let minimumFontSize = label['minimumFontSize'];
    let maximumFontSize = label['maximumFontSize'];

    let calculatedFontSize = height;

    if (maximumFontSize != null) {
        calculatedFontSize = maximumFontSize;
    } else {
        calculatedFontSize = defaultFontSize;
    }

    if (textAlignment == null)
        textAlignment = 'left';

    if (textColor == null) {
        textColor = { r: 0, g: 0, b: 0, a: 1 };
    } else {

        // if r, g, b, or a is null set them to 0, 0, 0, 1 respectively
        if (textColor.r == null) {
            textColor.r = 0
        }

        if (textColor.g == null) {
            textColor.g = 0
        }

        if (textColor.b == null) {
            textColor.b = 0
        }

        if (textColor.a == null) {
            textColor.a = 1
        }

        textColor = { r: +textColor.r, g: +textColor.g, b: +textColor.b, a: +textColor.a };
    }

    if (backgroundColor == null)
        backgroundColor = { r: 0, g: 0, b: 0, a: 0 };
    else
        backgroundColor = { r: +backgroundColor.r, g: +backgroundColor.g, b: +backgroundColor.b, a: +backgroundColor.a };


    if (!label) {
        return;
    }

    // if font name is not set, use the default font
    if (label['fontName']) {
        doc.setFont(label['fontFamily']);
    } else {
        doc.setFont('roboto');
    }

    if (colorOverride) {
        doc.setFillColor(colorOverride.r, colorOverride.g, colorOverride.b);
        doc.rect(x, y, +label['width'], +label['height'], 'F');
    }

    if (bold) {
        doc.setFont(undefined, 'bold')
    } else {
        doc.setFont(undefined, 'normal')
    }

    doc.setTextColor(textColor.r, textColor.g, textColor.b, textColor.a);
    doc.setFontSize(calculatedFontSize);
    let textWidth = doc.getTextWidth(text);

    if (!wrap) {
        // reduce font size until text fits
        while (textWidth > width && calculatedFontSize > 1) {
            calculatedFontSize--;
            doc.setFontSize(calculatedFontSize);
            textWidth = doc.getTextWidth(text);
        }
    } else {
        let textFits = false;
        while (!textFits) {

            // set the font size
            doc.setFontSize(calculatedFontSize);

            // for each word in text
            let textSplit = text.split(" ");

            let largestWidth = 0;

            let runningText = "";
            let final = "";

            // foreach word
            for (let word of textSplit) {
                // add word to running text
                let oldRunningText = runningText;
                runningText += word + " ";

                let runningTextWidth = doc.getTextWidth(runningText);

                // if running text is too long
                if (runningTextWidth > width) {
                    // add running text to final
                    final += oldRunningText + "\n";
                    // reset running text
                    runningText = word + " ";
                }

                // if running text width is larger than largest width
                if (runningTextWidth > largestWidth) {
                    largestWidth = runningTextWidth;
                }
            }

            // add final running text to final
            final += runningText;

            // determine text height
            let numLinebreaks = final.split("\n").length;
            let boxHeight = doc.getLineHeight() * PTToMM * numLinebreaks;

            if (boxHeight < height) {
                text = final;
                textWidth = largestWidth;

                textFits = true;

                // // draw debug box left of label that has a height calculated from the font size
                // doc.rect(x - 10, y, 10, boxHeight, 'D');
            } else {
                textFits = false;
                calculatedFontSize -= 0.5;
            }


        }
    }

    // // debug draw rectangle around label
    // doc.setFillColor(0, 0, 0);
    // doc.rect(x, y, width, height, 'D');




    if (textAlignment === 'center') {
        x = x + (width - textWidth) / 2;
    } else if (textAlignment === 'right') {
        x = x + width - textWidth;
    }

    if (!wrap) {
        // align text vertically
        y = y + ((+label['height']) / 2) - 1;
    }

    drawText(doc, calculatedFontSize, text.toString(), x, y, width);

    doc.setFont(undefined, 'normal')
}

//Fills a label as if it were a checkbox with a standard size and character
export function fillCheckboxAtLabel(doc, condition, labelName, labels) {
    if (condition) {
        drawTextOnLabel(doc, checkBoxCharacter, labelName, labels);
    }
}

export function signatureLabel(labelName, labels) {
    const label = labels['signatures'][labelName];

    if (!label) {
        throw new Error(`Signature label ${labelName} not found in labels`);
    }

    return label;
}

export const isMale = (gender) => {
    return gender == 1;
}

export const isFemale = (gender) => {
    return gender == 0;
}

export function isAnotherGender(gender) {
    return gender == 2;
}

export function formatDayFromDate(date) {
    // if no date return empty string
    if (!date) {
        return '';
    }

    return `${moment(date).date().toString().padStart(2, "0")}`;
}

export function formatMonthFromDate(date) {
    // if no date return empty string
    if (!date) {
        return '';
    }

    return `${(moment(date).month() + 1).toString().padStart(2, "0")}`;
}

export function formatYearFromDate(date) {
    // if no date return empty string
    if (!date) {
        return '';
    }

    return `${moment(date).year().toString().padStart(2, "0")}`;
}

export function isLanguageEnglish(i18n) {
    // console.log("language is", i18n.language)
    return !i18n.language.startsWith("fr");
}

export const signatureTab = (recipientId, documentId, pageNumber, signatureLabel, scale) => {

    scale = scale || 1.0;
    const width = +signatureLabel.width;
    const height = +signatureLabel.height;

    const textWidth = 12;

    let x = +signatureLabel.x;
    let y = +signatureLabel.y;
    //
    // // align text vertically
    // y = y + ((height) / 2) - 1;

    return {
        documentId: documentId,
        pageNumber: pageNumber,
        recipientId: recipientId,
        xPosition: x,
        yPosition: y,
        scale: scale
    }
}

export const initialsTab = (recipientId, documentId, pageNumber, signatureLabel, scale) => {
    return {
        ...signatureTab(recipientId, documentId, pageNumber, signatureLabel, scale),
        initials: true
    }
}

export function getProvinceLongNameForShortName(shortName) {
    console.log("getting long name for", shortName)

    if (shortName == "QC") // TODO added to deal with test data from backend
        shortName = "PQ"

    // foreach province in the list of provinces if province 'value' == shortName return the 'label'
    for (let province of PROVINCES) {
        if (province.value === shortName) {
            return province.label;
        }
    }

    console.log("NO LONG NAME FOUND FOR", shortName, PROVINCES)
}

export function getCustodianAddress(application) {
    let poBox = application.custodianPobox || "";
    let street = application.custodianAddress || "";
    let suite = application.custodianSuite || "";
    let streetNumber = application.custodianStreetNumber || "";

    let address = "";

    //trim all whitespace
    poBox = poBox.trim();
    street = street.trim();
    suite = suite.trim();
    streetNumber = streetNumber.trim();

    if (poBox !== "") {
        address += "PO Box " + poBox;
    }

    if (streetNumber !== "") {
        address += " " + streetNumber;
    }

    if (street !== "") {
        address += " " + street;
    }

    if (suite !== "") {
        address += " Suite " + suite;
    }

    address = address.trim();

    return address;
}

export function getSubscriber1Address(subscriber) {
    let poBox = subscriber.pobox || "";
    let street = subscriber.address || "";
    let suite = subscriber.suite || "";
    let streetNumber = subscriber.streetNumber || "";

    let address = "";

    //trim all whitespace
    poBox = poBox.trim();
    street = street.trim();
    suite = suite.trim();
    streetNumber = streetNumber.trim();

    if (poBox !== "") {
        address += "PO Box " + poBox;
    }

    if (streetNumber !== "") {
        address += " " + streetNumber;
    }

    if (street !== "") {
        address += " " + street;
    }

    if (suite !== "") {
        address += " Suite " + suite;
    }

    address = address.trim();

    return address;
}

export function isChildCareCheckExpense(kyc) {
    const isMaternityForm = kyc.kycFormType == 0;

    if (isMaternityForm) {
        return kyc.childCare >= kyc.childCareAfterMat; // ipad just checks kyc.childCare to kyc.childCare... TODO: check if this is correct
    }

    return false;
}

export function calculateDepositWithApplicationPlan1(state) {

    const plan = state.plan

    if (plan.planTypeID == PLAN_TYPE_IDS.TRANSFER) {
        return 0;
    }

    let quoteAttributes = generateQuote(state);

    // round to 2 fractional digits
    return quoteAttributes.initialDepositAmount.toFixed(2);
}

export function calculateDepositAmountPlan1(state) {

    const plan = state.plan

    if (plan.planTypeID == PLAN_TYPE_IDS.TRANSFER) {
        return 0;
    }

    let quoteAttributes = generateQuote(state);

    // round to 2 fractional digits
    return quoteAttributes.amountPerDeposit.toFixed(2);
}

export function calculateInterestDeficiencyPlan1(state) {
    const plan = state.plan

    if (plan.planTypeID == PLAN_TYPE_IDS.TRANSFER) {
        return 0;
    }

    let quoteAttributes = generateQuote(state);

    // round to 2 fractional digits
    return quoteAttributes.interestDeficiency.toFixed(2);
}


export function calculateDepositWithApplicationPlan2(state) {

    const plan = state.plan2

    if (plan.planTypeID == PLAN_TYPE_IDS.TRANSFER) {
        return 0;
    }

    let quoteAttributes = generateQuote(stateWithPlan2AsPrimary(state));

    // round to 2 fractional digits
    return quoteAttributes.initialDepositAmount.toFixed(2);
}

export function calculateDepositAmountPlan2(state) {

    const plan = state.plan2

    if (plan.planTypeID == PLAN_TYPE_IDS.TRANSFER) {
        return 0;
    }

    let quoteAttributes = generateQuote(stateWithPlan2AsPrimary(state));

    // round to 2 fractional digits
    return quoteAttributes.amountPerDeposit.toFixed(2);
}

export function calculateInterestDeficiencyPlan2(state) {
    const plan = state.plan2

    if (plan.planTypeID == PLAN_TYPE_IDS.TRANSFER) {
        return 0;
    }

    let quoteAttributes = generateQuote(stateWithPlan2AsPrimary(state));

    // round to 2 fractional digits
    return quoteAttributes.interestDeficiency.toFixed(2);
}

export function isCesgMaxedOut(state) {
    const plan = state.plan;
    const plan2 = state.plan2;

    let plan1TypeId = plan.planTypeID;
    let plan2TypeId = plan2.planTypeID;

    let plan1Amount = 0;
    let plan2Amount = 0;


    if (plan1TypeId == PLAN_TYPE_IDS.GROUP || plan1TypeId == PLAN_TYPE_IDS.ACHIEVERS || plan1TypeId == PLAN_TYPE_IDS.SI) {
        let quoteAttributes = generateQuote(state);

        // // if effective date is null throw an error
        // if (!quoteAttributes.effectiveDate) {
        //     throw new Error("Effective date is null");
        // }

        if (plan.depositMode == "T") {
            plan1Amount = quoteAttributes.initialDepositAmount;
        } else {
            plan1Amount = quoteAttributes.yearlyDepositAmount;
        }
    }


    if (plan2TypeId == PLAN_TYPE_IDS.GROUP || plan2TypeId == PLAN_TYPE_IDS.ACHIEVERS || plan2TypeId == PLAN_TYPE_IDS.SI) {
        let quoteAttributes = generateQuote(stateWithPlan2AsPrimary(state));

        // // if effective date is null throw an error
        // if (!quoteAttributes.effectiveDate) {
        //     throw new Error("Effective date is null");
        // }

        if (plan2.depositMode == "T") {
            plan2Amount = quoteAttributes.initialDepositAmount;
        } else {
            plan2Amount = quoteAttributes.yearlyDepositAmount;
        }
    }

    console.log("Plan 1 Amount: ", plan1Amount);
    console.log("Plan 2 Amount: ", plan2Amount);

    if (plan1Amount + plan2Amount >= 2500) {
        return true;
    }

    return false;

}
